import React, { createContext, useContext, ReactNode } from 'react';
import { useSocket } from './SocketContext';

interface UnifiedMessagingContextType {
  subscribeToOrder: (orderId: string) => void;
  unsubscribeFromOrder: (orderId: string) => void;
  subscribeToTable: (tableNumber: string, vendorId: string) => void;
  unsubscribeFromTable: (tableNumber: string, vendorId: string) => void;
  subscribeToKitchen: (kitchenId: number) => void;
  subscribeToVendor: (vendorId: number) => void;
  isConnected: () => boolean;
  preferredProtocol: 'socketio';
}

const UnifiedMessagingContext = createContext<UnifiedMessagingContextType | undefined>(undefined);

export const useUnifiedMessaging = () => {
  const context = useContext(UnifiedMessagingContext);
  if (!context) {
    throw new Error('useUnifiedMessaging must be used within a UnifiedMessagingProvider');
  }
  return context;
};

interface UnifiedMessagingProviderProps {
  children: ReactNode;
}

export const UnifiedMessagingProvider: React.FC<UnifiedMessagingProviderProps> = ({ children }) => {
  const socket = useSocket();

  // Always use Socket.IO for real-time updates
  const preferredProtocol = 'socketio' as const;

  const subscribeToOrder = (orderId: string) => {
    console.log(`Subscribing to order ${orderId} updates via Socket.IO`);
    socket.subscribeToOrder(parseInt(orderId));
  };

  const unsubscribeFromOrder = (orderId: string) => {
    console.log(`Unsubscribing from order ${orderId}`);
    socket.unsubscribeFromOrder(parseInt(orderId));
  };

  const subscribeToTable = (tableNumber: string, vendorId: string) => {
    console.log(`Subscribing to table ${tableNumber} for vendor ${vendorId}`);
    socket.subscribeToTable(tableNumber, parseInt(vendorId));
  };

  const unsubscribeFromTable = (tableNumber: string, vendorId: string) => {
    console.log(`Unsubscribing from table ${tableNumber}`);
    socket.unsubscribeFromTable(tableNumber, parseInt(vendorId));
  };

  const subscribeToKitchen = (kitchenId: number) => {
    console.log(`Subscribing to kitchen ${kitchenId} via Socket.IO`);
    socket.subscribeToKitchen(kitchenId);
  };

  const subscribeToVendor = (vendorId: number) => {
    console.log(`Subscribing to vendor ${vendorId} via Socket.IO`);
    socket.subscribeToVendor(vendorId);
  };

  const isConnected = () => {
    return socket.isConnected;
  };

  const value: UnifiedMessagingContextType = {
    subscribeToOrder,
    unsubscribeFromOrder,
    subscribeToTable,
    unsubscribeFromTable,
    subscribeToKitchen,
    subscribeToVendor,
    isConnected,
    preferredProtocol,
  };

  return (
    <UnifiedMessagingContext.Provider value={value}>
      {children}
    </UnifiedMessagingContext.Provider>
  );
};